// The two marks of the NPC side (round 088 card 03 a, v2-the-deep.md §8,
// §18.2 slot 2, R1805, R2637): the swallowtail hull of a pirate group and
// the ragged ring of a monster.
//
// ONE geometry serves every surface. The NPC SIDE row of the legend and
// the head of an NPC card draw it as SVG (`ui/chart/faction-key.ts`,
// `ui/cards/marks.ts`), and the fleet layer bakes it into the atlas cells
// `npc:pirate` and `npc:monster` (`fast/gfx/atlas.ts`), so the side keeps
// its shape on every mark. The geometry lives in a box of
// `NPC_BOX` units with its centre at the middle, and the bow points east.
//
// A pirate hull is the INVERSE HULL (R2874, mockup round 134 card 01): the
// body fills in the deep ink and its rim and its eye stand in the NPC ink,
// so the side keeps its ink on the rim and does not share the white body
// of the own hull mark `white-core`. The ragged ring of a monster is a
// light line round a dark core already, so it stays as it is.

import { Brotherhood, ShipClass } from '../gen/proto/aquaphobia';
import { FLEET_NPC_MARK_PX, FLEET_NPC_RIM_PX } from '../gen/presentation';
import { COMBAT_WINGS } from './marks';

/** The side of the box the geometry is drawn in. */
export const NPC_BOX = 24;

/** A point of the box. */
export type NpcPoint = readonly [number, number];

/** The swallowtail: a hull with a forked stern, bow east, as one polygon. */
const SWALLOWTAIL: readonly NpcPoint[] = [
  [21, 12], [5.5, 5], [8.5, 10], [4, 10], [6.5, 12], [4, 14], [8.5, 14], [5.5, 19],
];

/** The eye of the swallowtail, in the rim ink. */
export const SWALLOWTAIL_EYE = { x: 13, y: 12, r: 1.6 } as const;

/** One arm of the ragged ring: a quadratic curve from the ring outward. */
export interface NpcArm { x0: number; y0: number; cx: number; cy: number; x1: number; y1: number }

/** The ragged ring: six arms, a notched ring and an eye. */
export interface MonsterGeometry {
  arms: readonly NpcArm[];
  ring: readonly NpcPoint[];
  eye: { x: number; y: number; r: number };
}

/** The stroke widths of the SVG marks, in box units: the light rim of the
 *  inverse hull of the swallowtail (R2874), an arm of the ring and the rim
 *  of the ring. The atlas cell of a pirate strokes `FLEET_NPC_RIM_PX`. */
export const NPC_STROKE = { rim: 1.4, line: 1.4, ring: 1.2 } as const;

/** The ring of a monster, computed once. */
export const MONSTER: MonsterGeometry = (() => {
  const c = NPC_BOX / 2;
  const arms: NpcArm[] = [];
  for (let i = 0; i < 6; i++) {
    const a = i * Math.PI / 3 + 0.3;
    arms.push({
      x0: c + Math.cos(a) * 5, y0: c + Math.sin(a) * 5,
      cx: c + Math.cos(a + 0.5) * 8.5, cy: c + Math.sin(a + 0.5) * 8.5,
      x1: c + Math.cos(a + 0.2) * 10.5, y1: c + Math.sin(a + 0.2) * 10.5,
    });
  }
  const ring: NpcPoint[] = [];
  for (let i = 0; i <= 24; i++) {
    const b = i / 24 * Math.PI * 2, r = 4.6 + (i % 2 ? 0.8 : 0);
    ring.push([c + Math.cos(b) * r, c + Math.sin(b) * r]);
  }
  return { arms, ring, eye: { x: c, y: c, r: 1.4 } };
})();

const fig = (n: number) => n.toFixed(1);

/** An SVG path through the points, closed. */
function polyPath(pts: readonly NpcPoint[]): string {
  return `${pts.map(([x, y], i) => `${i ? 'L' : 'M'}${fig(x)} ${fig(y)}`).join('')}Z`;
}

/** The SVG path data of the three parts: the swallowtail, the arms and
 *  the ring of a monster. The legend row and the card head mark write
 *  their own elements round them. */
export const NPC_PATHS = {
  swallowtail: polyPath(SWALLOWTAIL),
  arms: MONSTER.arms.map((a) => `M${fig(a.x0)} ${fig(a.y0)}Q${fig(a.cx)} ${fig(a.cy)} ${fig(a.x1)} ${fig(a.y1)}`).join(''),
  ring: polyPath(MONSTER.ring),
} as const;

// ---------------------------------------------------------------------------
// The lean swallowtail and the five brotherhood marks (mockup round 113,
// v2-the-deep.md §8.5, R2720)
// ---------------------------------------------------------------------------
//
// Every pirate hull stands on the LEAN swallowtail: longer and half as wide
// as the swallowtail above, with short barbs and a deeper notch. A combat
// class takes the two wings of its class at the points of the regular fleet
// chevron (R426). A support class takes its part of `SUPPORT_EXTRAS` (R835),
// fitted to the lean base, so a taken support hull shows its class (ruling
// R-h). A hull whose class the server withheld keeps the swallowtail above
// (R412). The brotherhood mark comes on top: card 01 Stern Lantern, 06 Jaw
// Cut, 11 Knot Lights, 13 Grapnel Stern and 18 Undertow Vee, one for each
// row of `[[npc.brotherhoods]]`.
//
// The figures below are the figures of the round page, in MAP px with the
// centre of the box at the origin and the bow east. The page scales the
// 24-unit box by 26/24, which is `FLEET_NPC_MARK_PX / NPC_BOX`.

/** The lean swallowtail in the box of `NPC_BOX` units (round 113). */
const LEAN_SWALLOWTAIL: readonly NpcPoint[] = [
  [23.5, 12], [6.5, 8.4], [9.5, 10.5], [2.5, 10], [6.5, 12], [2.5, 14], [9.5, 13.5], [6.5, 15.6],
];

/** The eye of the lean swallowtail, in the box. */
const LEAN_EYE = { x: 14, y: 12, r: 1.2 } as const;

/** The scale of the box to map px. */
const SWS = FLEET_NPC_MARK_PX / NPC_BOX;

/** The points a mark reads off one swallowtail, in map px. */
export interface SwallowGeo {
  /** The polygon, centred. */
  pts: readonly NpcPoint[];
  /** The x of the bow. */
  bow: number;
  /** A barb tip, on the +y side. */
  barb: NpcPoint;
  /** A prong tip of the fork, on the +y side. */
  tip: NpcPoint;
  /** The x of the eye and its radius. */
  eye: NpcPoint;
  lean: boolean;
}

function geoOf(box: readonly NpcPoint[], eye: { x: number; y: number; r: number }, lean: boolean): SwallowGeo {
  const c = NPC_BOX / 2;
  const pts = box.map(([x, y]): NpcPoint => [(x - c) * SWS, (y - c) * SWS]);
  return {
    pts, bow: pts[0][0], barb: [pts[1][0], -pts[1][1]], tip: [pts[3][0], -pts[3][1]],
    eye: [(eye.x - c) * SWS, eye.r * SWS], lean,
  };
}

/** The lean swallowtail of every pirate hull whose class is known. */
export const LEAN_GEO: SwallowGeo = geoOf(LEAN_SWALLOWTAIL, LEAN_EYE, true);
/** The swallowtail of R2637, which a hull with no class keeps (R412). */
export const TODAY_GEO: SwallowGeo = geoOf(SWALLOWTAIL, SWALLOWTAIL_EYE, false);

/** The half width of a swallowtail at `x`, on the line from the bow to a barb. */
export function halfWidth(g: SwallowGeo, x: number): number {
  return g.barb[1] * (g.bow - x) / (g.bow - g.barb[0]);
}

/** The path calls a painter makes; a 2D context answers all of them. */
export type NpcPen = Pick<CanvasRenderingContext2D, 'moveTo' | 'lineTo' | 'arc' | 'closePath'>;

/**
 * One polygon as a sub-path. Every poly winds the same way, as the page
 * winds them, so the nonzero rule adds two parts that touch.
 */
function poly(c: NpcPen, p: readonly number[]): void {
  let a = 0;
  for (let i = 0; i < p.length; i += 2) {
    const j = (i + 2) % p.length;
    a += p[i] * p[j + 1] - p[j] * p[i + 1];
  }
  const q: number[] = [];
  if (a > 0) q.push(...p);
  else for (let i = p.length - 2; i >= 0; i -= 2) q.push(p[i], p[i + 1]);
  c.moveTo(q[0], q[1]);
  for (let i = 2; i < q.length; i += 2) c.lineTo(q[i], q[i + 1]);
  c.closePath();
}

function bar(c: NpcPen, x0: number, y0: number, x1: number, y1: number, w: number): void {
  const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (w / 2), ny = (dx / len) * (w / 2);
  poly(c, [x0 + nx, y0 + ny, x1 + nx, y1 + ny, x1 - nx, y1 - ny, x0 - nx, y0 - ny]);
}

function band(c: NpcPen, x: number, y: number, ro: number, ri: number, a0: number, a1: number): void {
  c.moveTo(x + Math.cos(a0) * ro, y + Math.sin(a0) * ro);
  c.arc(x, y, ro, a0, a1);
  c.arc(x, y, ri, a1, a0, true);
  c.closePath();
}

function ring(c: NpcPen, x: number, y: number, ro: number, ri: number): void {
  c.moveTo(x + ro, y);
  c.arc(x, y, ro, 0, Math.PI * 2);
  c.closePath();
  c.moveTo(x + ri, y);
  c.arc(x, y, ri, 0, Math.PI * 2, true);
  c.closePath();
}

function dot(c: NpcPen, x: number, y: number, r: number): void {
  c.moveTo(x + r, y);
  c.arc(x, y, r, 0, Math.PI * 2);
  c.closePath();
}

/** The two parts of a wing pair: the +y part and its mirror. */
function wingPath(c: NpcPen, w: readonly number[]): void {
  poly(c, w);
  const m: number[] = [];
  for (let i = 0; i < w.length; i += 2) m.push(w[i], -w[i + 1]);
  poly(c, m);
}

/** The Sentinel bars of `SUPPORT_EXTRAS` (+y side): they sit outside the
 *  barbs, as a wing does, so the lean base takes them as they are. */
const SENTINEL_BAR = [-6.9, 7.6, 3.1, 5.6, 3.5, 3.4, -6.5, 5.4] as const;

/** One support part fitted to the lean base (round 113), with the last x
 *  of the part on the axis, where a stern mark begins. */
interface LeanPart { aft?: number; path(c: NpcPen): void }

const LEAN_SUPPORT = new Map<number, LeanPart>([
  // Vedette, Feather: the outer quills grow from the barb tips; the centre
  // quill stands behind the prong tips, so the notch stays open.
  [ShipClass.SHIP_CLASS_VEDETTE, { aft: -15.0, path(c) {
    bar(c, -6.6, -3.2, -12.4, -5.8, 1.1);
    bar(c, -6.6, 3.2, -12.4, 5.8, 1.1);
    bar(c, -11.6, 0, -15.0, 0, 1.1);
  } }],
  // Dredger, Tow Line: a bridle from the two prong tips takes the tow.
  [ShipClass.SHIP_CLASS_DREDGER, { aft: -19.4, path(c) {
    const [tx, ty] = LEAN_GEO.tip;
    bar(c, tx, -ty, -12.8, 0, 0.9);
    bar(c, tx, ty, -12.8, 0, 0.9);
    bar(c, -15.0, 0, -17.2, 0, 1.0);
    for (const x of [-12.8, -17.2]) {
      bar(c, x, -2.8, x, 2.8, 1.0);
      bar(c, x - 2.2, -2.8, x - 2.2, 2.8, 1.0);
      bar(c, x, -2.8, x - 2.2, -2.8, 1.0);
      bar(c, x, 2.8, x - 2.2, 2.8, 1.0);
    }
  } }],
  // Beacon, the radial: the eye is the hub; six spokes and no aft spoke.
  [ShipClass.SHIP_CLASS_BEACON, { path(c) {
    const hx = LEAN_GEO.eye[0];
    for (const a of [Math.PI / 4, Math.PI / 2, (3 * Math.PI) / 4]) {
      for (const s of [1, -1]) {
        bar(c, hx + Math.cos(a) * 3, s * Math.sin(a) * 3, hx + Math.cos(a) * 7.2, s * Math.sin(a) * 7.2, 1.1);
      }
    }
  } }],
  // Oracle, Open Arcs: 0.5 px aft of the needle's arcs, the bow between them.
  [ShipClass.SHIP_CLASS_ORACLE, { path(c) {
    band(c, 0.5, 0, 11.4, 10.4, -0.85, 0.85);
    band(c, 0.5, 0, 15.2, 14.2, -0.85, 0.85);
  } }],
  // Tender, the lifebuoy: the ring round the eye, the barbs outside it.
  [ShipClass.SHIP_CLASS_TENDER, { path(c) { ring(c, 0.5, 0, 6, 4.6); } }],
  // Sapper, Mine Rails: the rails clear of the prongs, the mine behind.
  [ShipClass.SHIP_CLASS_SAPPER, { aft: -18.0, path(c) {
    bar(c, -7.2, 3.6, -13.6, 3.6, 1.05);
    bar(c, -7.2, -3.6, -13.6, -3.6, 1.05);
    dot(c, -16.4, 0, 1.6);
  } }],
  // Sentinel, Aegis Wing: the two swept bars, as on the needle.
  [ShipClass.SHIP_CLASS_SENTINEL, { path(c) { wingPath(c, SENTINEL_BAR); } }],
]);

const LEAN_WINGS = new Map<number, readonly number[]>(COMBAT_WINGS);

/**
 * The class part of a pirate hull on the lean base, or null for a class
 * with no fit, which draws the plain lean swallowtail. A pirate never
 * sails the Commodore, so its forked wing and its star have no fit.
 */
export function leanPartOf(cls: number): ((c: NpcPen) => void) | null {
  const w = LEAN_WINGS.get(cls);
  if (w) return (c) => wingPath(c, w);
  return LEAN_SUPPORT.get(cls)?.path ?? null;
}

/** The geometry a pirate hull of class `cls` stands on (R412). */
export function pirateGeo(cls: number): SwallowGeo {
  return cls === ShipClass.SHIP_CLASS_UNSPECIFIED ? TODAY_GEO : LEAN_GEO;
}

/** The last x of a pirate hull on the axis, where a stern mark begins. */
export function pirateAft(cls: number): number {
  return LEAN_SUPPORT.get(cls)?.aft ?? pirateGeo(cls).tip[0];
}

/** A light of a brotherhood mark: a cell the layer draws at an alpha each
 *  frame, apart from the baked hull (card 01 and card 11). */
export type PirateLight = 'lantern' | 'knot';

/** The light of a brotherhood, or null for a mark with none. */
export function pirateLightOf(bro: number): PirateLight | null {
  if (bro === Brotherhood.BROTHERHOOD_LANTERN_KIN) return 'lantern';
  if (bro === Brotherhood.BROTHERHOOD_NINEFATHOM) return 'knot';
  return null;
}

const lightXs = new Map<number, readonly number[]>();
/** The x of each light of a brotherhood on a hull of `cls`: one lantern
 *  6.5 px behind the stern, or three knot lights from 2.5 px behind it,
 *  4.5 px apart. The fleet layer asks each lit hull a frame, so the list is
 *  built once a pair (§10.4). */
export function pirateLightXs(bro: number, cls: number): readonly number[] {
  const key = bro * 0x10000 + cls;
  let xs = lightXs.get(key);
  if (xs === undefined) {
    const aft = pirateAft(cls);
    xs = bro === Brotherhood.BROTHERHOOD_LANTERN_KIN ? [aft - 6.5]
      : bro === Brotherhood.BROTHERHOOD_NINEFATHOM ? [aft - 2.5, aft - 7, aft - 11.5]
        : [];
    lightXs.set(key, xs);
  }
  return xs;
}

/**
 * The alpha of light `k` at the layer time `t` (wall s), with `seed` the
 * phase of the hull. The lantern blinks slowly (card 01). The three knot
 * lights light one after the other from the hull aft, then pause (card
 * 11). With reduced motion every light holds at 1.
 */
export function pirateLightAlpha(bro: number, k: number, t: number, seed: number, motion: boolean): number {
  if (!motion) return 1;
  if (bro === Brotherhood.BROTHERHOOD_LANTERN_KIN) return 0.45 + 0.55 * Math.abs(Math.sin(1.7 * t + seed));
  const ph = (((t * 0.8 + seed) % 1) + 1) % 1;
  return Math.floor(ph * 4) === k ? 1 : 0.25;
}

/** The inks of a bake: the light parts at an alpha (the rim, the marks on
 *  the body, the gear and the lights), and the deep ink of the body. */
export interface PirateInks {
  light: (a: number) => string;
  deep: (a: number) => string;
}

function stroke(ctx: CanvasRenderingContext2D, style: string, w: number): void {
  ctx.lineWidth = w;
  ctx.strokeStyle = style;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
}

/** The bridle: two hairlines from the prong tips meet 2.2 px behind the
 *  fork, and one hairline runs on to `x1`. The notch stays open water. */
function tether(ctx: CanvasRenderingContext2D, g: SwallowGeo, x1: number, a: number, ink: PirateInks): void {
  const [tx, ty] = g.tip, j = tx - 2.2;
  ctx.beginPath();
  ctx.moveTo(tx + 0.3, -ty); ctx.lineTo(j, 0); ctx.lineTo(tx + 0.3, ty);
  ctx.moveTo(j, 0); ctx.lineTo(Math.min(j, x1), 0);
  stroke(ctx, ink.light(a), 0.7);
}

/**
 * Paints one pirate hull cell in map px about the origin of the hull: the
 * parts under the hull, the light rim, the body, the class part, the eye
 * and the mark on the hull. The lights of card 01 and card 11 are not in
 * the cell: `paintPirateLight` paints them, and the layer draws them at an
 * alpha each frame.
 *
 * The hull is the INVERSE HULL (R2874, mockup round 134 card 01): the body
 * and the class part fill in the deep ink, a light rim of
 * `FLEET_NPC_RIM_PX` stands round them, and the marks on the body (the
 * eye, the jaw bites, the vee) take the light ink, so they stay on the
 * dark body. The gear astern keeps its inks.
 */
export function paintPirateHull(ctx: CanvasRenderingContext2D, cls: number, bro: number, ink: PirateInks): void {
  const g = pirateGeo(cls);
  const part = g.lean ? leanPartOf(cls) : null;
  const aft = pirateAft(cls);
  const flat = g.pts.flat();
  const body = () => { ctx.beginPath(); poly(ctx, flat); };
  const parts = () => { ctx.beginPath(); part!(ctx); };
  const edge = ink.deep(0.9);
  // Under the hull: the bridle and the gear of a stern mark.
  if (bro === Brotherhood.BROTHERHOOD_LANTERN_KIN) tether(ctx, g, aft - 3.5, 0.85, ink);
  else if (bro === Brotherhood.BROTHERHOOD_NINEFATHOM) tether(ctx, g, aft - 12.5, 0.4, ink);
  else if (bro === Brotherhood.BROTHERHOOD_GRAPNEL_COMPANY) {
    // Card 13 Grapnel Stern: a shaft from the bridle, two hooks forward.
    const e = aft - 6, tx = g.tip[0];
    tether(ctx, g, tx - 2.2, 0.85, ink);
    const grapnel = () => {
      ctx.beginPath(); ctx.moveTo(tx - 2.2, 0); ctx.lineTo(e, 0);
      for (const s of [-1, 1]) {
        ctx.moveTo(e, 0); ctx.quadraticCurveTo(e - 0.6, s * 3.8, e + 3.2, s * 4.4); ctx.lineTo(e + 2.3, s * 2.9);
      }
    };
    grapnel(); stroke(ctx, edge, 1.1 + 1.8);
    grapnel(); stroke(ctx, ink.light(1), 1.1);
  }
  // The hull: the light rim under, then the body, then the class part,
  // each filled alone in the deep ink, so the body shows through the ring
  // of the Tender. The rim strokes twice its width under the fill, so its
  // width stands outside the body and no rim line crosses the join of a
  // part and the body.
  const rim = ink.light(1), w = 2 * FLEET_NPC_RIM_PX;
  body(); stroke(ctx, rim, w);
  if (part) { parts(); stroke(ctx, rim, w); }
  ctx.fillStyle = ink.deep(1);
  body(); ctx.fill('nonzero');
  if (part) { parts(); ctx.fill('nonzero'); }
  // The eye in the light ink, whose place the Undertow Vee takes.
  if (bro !== Brotherhood.BROTHERHOOD_UNDERTOW) {
    ctx.beginPath(); ctx.arc(g.eye[0], 0, g.eye[1], 0, Math.PI * 2);
    ctx.fillStyle = ink.light(0.9); ctx.fill();
  }
  // On the hull.
  if (bro === Brotherhood.BROTHERHOOD_SALTJAW) {
    // Card 06 Jaw Cut: two bites in each flank, light on the deep body.
    const d = g.lean ? 1.2 : 1.4;
    ctx.save(); body(); ctx.clip();
    ctx.fillStyle = ink.light(0.95);
    for (const x of g.lean ? [5.4, 0.6] : [2, -3]) {
      for (const s of [-1, 1]) {
        ctx.beginPath();
        poly(ctx, [x + 1.6, s * (halfWidth(g, x + 1.6) + 0.4), x, s * (halfWidth(g, x) - d),
          x - 1.6, s * (halfWidth(g, x - 1.6) + 0.4)]);
        ctx.fill();
      }
    }
    ctx.restore();
  } else if (bro === Brotherhood.BROTHERHOOD_UNDERTOW) {
    // Card 18 Undertow Vee: two chevrons cross the hull and its part, light
    // on the deep body.
    const o = g.lean ? 3 : 0;
    const vee = () => {
      for (const j of [0, 4.5]) {
        ctx.beginPath();
        ctx.moveTo(3.5 + o - j, -16); ctx.lineTo(-2.5 + o - j, 0); ctx.lineTo(3.5 + o - j, 16);
        stroke(ctx, ink.light(0.95), 1.3);
      }
    };
    ctx.save(); body(); ctx.clip(); vee(); ctx.restore();
    if (part) { ctx.save(); parts(); ctx.clip(); vee(); ctx.restore(); }
  }
}

/**
 * Paints one light about the origin, in map px: the lantern of card 01 (a
 * glow, a diamond with a dark edge and a core) or one knot light of card
 * 11 (a glow, a light and a core).
 */
export function paintPirateLight(ctx: CanvasRenderingContext2D, light: PirateLight, ink: PirateInks): void {
  const glow = (r: number, a: number) => {
    const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    gr.addColorStop(0, ink.light(a));
    gr.addColorStop(1, ink.light(0));
    ctx.fillStyle = gr;
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
  };
  const disc = (r: number) => {
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = ink.light(1); ctx.fill();
  };
  if (light === 'lantern') {
    glow(5, 0.35);
    ctx.beginPath(); poly(ctx, [3, 0, 0, -2.1, -3, 0, 0, 2.1]);
    ctx.fillStyle = ink.light(1); ctx.fill();
    stroke(ctx, ink.deep(0.9), 0.7);
    disc(0.8);
    return;
  }
  glow(3.4, 0.4);
  disc(1.1);
  disc(0.5);
}
